import React, { useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import { useForm } from 'react-hook-form';
import {
  Routes, Route, useLocation,
  useParams,
  useNavigate,
  NavLink,
} from 'react-router-dom';
import { format } from 'date-fns';
import { CiCircleCheck } from 'react-icons/ci';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import ColorScale from 'color-scales';
import { BiPlus } from 'react-icons/bi';
import { IoChevronDown } from 'react-icons/io5';
import styles from './estimate.module.scss';
import {
  useGetEstimate, useCreateEstimatePhase,
  useGetEstimatesList,
  useCreateEstimateVarious,
  useUpdateEstimate,
  useDuplicateEstimatePhase,
  useReorderEstimatePhases,
} from '~/hooks/estimates';

import DraggablePhaseTabs from '~/components/shared/DraggablePhaseTabs';
import PhaseHeaderRates from '~/components/shared/PhaseHeaderRates';

import Loader from '~/components/Loader';
import GeneralSidebar from '~/components/estimate/GeneralSidebar';
import PhaseSidebar from '~/components/estimate/PhaseSidebar';

import GeneralInfos from './general-informations';
import Phase from './phase';
import BackButton from '~/components/BackButton';

import {
  StyledSelect,
} from '~/lib/HooksFormFields';
import { IPhase, IVarious } from '~/types/estimates';
import Various from './various';
import { useUserProfile } from '~/hooks/auth';
import { IOption } from '~/types/options';

// devis/65defe5c41dbeb16e77cde00/ -> info général
// devis/65defe5c41dbeb16e77cde00/O/dimension

const colorScale = new ColorScale(0, 100, ['#EAEDFA', '#acaeb6']);

const Estimate = () => {
  const { id, phaseId, ...params } = useParams();
  const location = useLocation();
  const [addStep, setAddStep] = useState<boolean>(false);
  const [addStepDown, setAddStepDown] = useState<boolean>(false);
  const [selectStep, setSelectStep] = useState<boolean>(false);
  const navigate = useNavigate();

  const { data: userAuth } = useUserProfile();

  const {
    isLoading,
    data: estimate,
  } = useGetEstimate(id || '');

  const countPhases = estimate?.phases ? estimate.various.length + estimate.phases.length : 0;

  const currentPhase = useMemo(() => {
    const found = estimate?.phases.find((d: IPhase) => d._id === params['*']);
    return found;
  }, [estimate, params]);

  const currentVarious = useMemo(() => {
    const found = estimate?.various.find((d: IVarious) => d._id === params['*']?.replace('various/', ''));
    return found;
  }, [estimate, params]);

  const {
    mutateAsync: createEstimatePhase,
  } = useCreateEstimatePhase();

  const {
    mutateAsync: createEstimateVarious,
  } = useCreateEstimateVarious();

  const {
    mutateAsync: duplicateEstimatePhase,
  } = useDuplicateEstimatePhase();

  const { mutateAsync: reorderEstimatePhases } = useReorderEstimatePhases();

  const {
    control,
    getValues,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      status: estimate?.status,
      worksite: {
        materialUsed: 'Multidirectionnel',
      },
    },
  });

  const { data: list = {} } = useGetEstimatesList();
  const { status } = list;
  const {
    mutate: updateEstimate,
  } = useUpdateEstimate('Les modifications ont été prise en compte');

  const estimateStatus = watch('status') || estimate?.status;
  const canEditSentStatus = estimateStatus === 'sent' || estimateStatus === 'archived';
  const sentStatusOptions = useMemo(
    () => status?.filter((s: IOption) => (
      s.value === 'sent'
      || s.value === 'refused'
      || s.value === 'validated'
      || s.value === 'archived'
    )),
    [status],
  );

  const sharedAccess = estimate?.sharedWith?.find(
    (share) => share.user._id === userAuth?._id,
  )?.access;
  const isSharedReadOnly = sharedAccess === 'read';

  const isPreview = isSharedReadOnly
    || estimate?.status === 'validated'
    || estimate?.status === 'sent'
    || estimate?.status === 'refused'
    || estimate?.status === 'archived'
    || (estimate?.status === 'review-pending' && estimate.author._id === userAuth?._id);

  useEffect(() => {
    if (!getValues('status') && estimate?.status) {
      setValue('status', estimate.status);
    }
  }, [estimate]);

  const generalInfosActive = useMemo(() => (
    location.pathname.split('/').length < 4
    || location.pathname.includes('/various')
  ), [location.pathname]);

  const handleCreatePhase = async (type: string = '') => {
    if (!id) return;
    try {
      const { estimate: updatedEstimate } = await createEstimatePhase({ estimateId: id, type });
      const phases: IPhase[] = updatedEstimate?.phases || [];
      navigate(`/estimates/${id}/${phases[phases.length - 1]._id}`);
      const layout = document?.getElementById('layout');
      if (layout) {
        layout.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    } catch {
      toast.error('La phase n\'a pu être créé');
    }
  };

  const handleDuplicatePhase = async () => {
    if (!id || !currentPhase?._id) return;
    try {
      const { estimate: updatedEstimate } = await duplicateEstimatePhase({
        estimateId: id,
        phaseId: currentPhase?._id,
      });
      const phases: IPhase[] = updatedEstimate?.phases || [];
      navigate(`/estimates/${id}/${phases[phases.length - 1]._id}`);
      const layout = document?.getElementById('layout');
      if (layout) {
        layout.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    } catch {
      toast.error('La phase n\'a pu être créé');
    }
  };

  const handleCreateVarious = async () => {
    if (!id) return;
    try {
      const { estimate: updatedEstimate } = await createEstimateVarious(id);
      const various: IVarious[] = updatedEstimate?.various || [];
      navigate(`/estimates/${id}/various/${various[various.length - 1]._id}`);
    } catch {
      toast.error('la phase divers n\'a pu être créé');
    }
  };

  const getIsActive = (pathname: string) => {
    let active = false;
    if (location.pathname === `/estimates/${id}${pathname}`) active = true;
    return active;
  };

  const handleReorderPhases = async (phaseIds: string[]) => {
    if (!id || isPreview) return;
    await reorderEstimatePhases({ estimateId: id, phaseIds });
  };

  useEffect(() => {
    if (estimate?._id) {
      reset(estimate);
    }
  }, [estimate]);

  return (
    <div className={styles.estimate}>
      {isLoading && (
        <div className={styles.loader}><Loader /></div>
      )}
      {estimate && (
        <div className={styles.content}>
          <StickyBox>
            <div className={styles.sidebar}>
              <div className={styles.back}>
                <BackButton path='/estimates' />
                <NavLink to='/estimates'>Retour à la liste des devis</NavLink>
              </div>
              <div className={styles.updatedAt}>
                {CiCircleCheck({ size: 20 })}
                <p>Dernière modification {format(estimate.updatedAt, "dd.MM.yyyy à HH'h'mm", { locale: fr })}</p>
              </div>
              <div className={styles.status}>
                <label>Statut</label>
                <StyledSelect
                  name='status'
                  control={control}
                  options={canEditSentStatus ? sentStatusOptions : status}
                  isDisabled={!canEditSentStatus}
                  handleChange={(opt:any) => {
                    updateEstimate({ _id: estimate?._id, status: opt.value });
                  }}
                  withColorDot
                />
              </div>
              <hr />
              {generalInfosActive
                ? <GeneralSidebar
                  estimate={estimate}
                  isPreview={isPreview}
                />
                : <PhaseSidebar
                  isPreview={isPreview}
                />
              }
            </div>
          </StickyBox>
          <div className={styles.containers}>
            <div className={styles.navTabs}>
              <NavLink
                to=''
                style={{
                  position: 'relative',
                  zIndex: (estimate?.phases?.length || 0) + 101,
                  backgroundColor: getIsActive('') ? 'white' : colorScale.getColor(0).toRGBString(),
                }}
              >
                Informations
              </NavLink>
              {countPhases >= 3
                && <div className={styles.step}>
                {!selectStep
                  ? <a
                    onMouseOver={() => {
                      setSelectStep(true);
                    }}
                    className={currentPhase?.name || currentVarious?.name ? styles.active : ''}
                  >
                    {currentPhase?.name || currentVarious?.name
                    || (estimate?.phases.length > 0 && estimate?.phases[0]?.name)
                    || (estimate?.various.length > 0 && estimate?.various[0]?.name)
                    }
                    <div className={styles.icon}
                      onMouseOver={() => {
                        setSelectStep(true);
                      }}
                    >
                      {IoChevronDown({})}
                    </div>

                  </a>
                  : <div
                    className={styles['select-step']}
                    onMouseLeave={() => setSelectStep(false)}
                    onClick={() => setSelectStep(false)}
                  >
                    <DraggablePhaseTabs
                      phases={estimate?.phases || []}
                      pathPrefix={`/estimates/${id}`}
                      getIsActive={getIsActive}
                      isPreview={isPreview}
                      colorScale={colorScale}
                      tabDenominator={estimate.phases.length + estimate.various.length}
                      getPhaseLabel={(phase, index) => phase.name || `Phase ${index + 1}`}
                      onReorder={handleReorderPhases}
                      layoutStyles={styles}
                      variant="dropdown"
                    />
                     {(estimate?.various || []).map((various: any, index: number) => (
                    <NavLink
                      to={`various/${various._id}`}
                      key={`phase-${various._id}`}
                        className={styles.option}
                      >
                      {various.name || `Phase ${index + 1}`}
                    </NavLink>
                     ))}
                  </div>
                }
                </div>
              }
              {countPhases > 0 && countPhases < 3
              && <>
                <DraggablePhaseTabs
                  phases={estimate?.phases || []}
                  pathPrefix={`/estimates/${id}`}
                  getIsActive={getIsActive}
                  isPreview={isPreview}
                  colorScale={colorScale}
                  tabDenominator={estimate.phases.length + estimate.various.length}
                  getPhaseLabel={(phase, index) => phase.name || `Phase ${index + 1}`}
                  onReorder={handleReorderPhases}
                  layoutStyles={styles}
                  variant="tabs"
                />
                {(estimate?.various || []).map((various: any, index: number) => (
                  <NavLink
                    to={`various/${various._id}`}
                    key={`phase-${various._id}`}
                    style={{
                      position: 'relative',
                      zIndex: (estimate.various.length + estimate.phases.length) - index,
                      boxShadow: getIsActive(`/various/${various._id}`) ? 'none' : '0px 18px 32px rgba(208, 210, 218, 0.25)',
                      backgroundColor: getIsActive(`/various/${various._id}`) ? 'white' : colorScale.getColor(
                        (100 / (estimate.phases.length + estimate.various.length))
                        * (estimate.phases.length + index + 1),
                      ).toRGBString(),
                    }}
                  >
                    {various.name || `Phase ${index + 1}`}
                  </NavLink>
                ))}
                </>
              }
              {!isPreview
                && estimate?.worksite?.name
                && estimate?.worksite?.city
                && estimate?.worksite?.department
                && estimate?.logistics?.operationalBase
              && <>
                {!addStep
                  ? <a className={styles.add} onMouseOver={() => setAddStep(true)}>
                    {BiPlus({})} Ajouter une phase
                  </a>
                  : <div
                    className={styles['add-step']}
                    onMouseLeave={() => setAddStep(false)}
                  >
                    <div className={styles.select}>
                      <div className={styles.label}>
                      {BiPlus({})} Ajouter une phase
                    </div>
                    <div className={styles.option} onClick={() => handleCreatePhase()}>
                      Phase N° {estimate.phases.length + 1}
                    </div>
                    <div className={styles.option} onClick={() => handleCreatePhase('elevator')}>
                      Phase ascenseur
                    </div>
                    <div className={styles.option} onClick={() => handleCreatePhase('umbrella')}>
                      Phase parapluie
                    </div>

                    <div className={styles.option} onClick={() => handleCreateVarious()}>
                      Phase divers
                    </div>
                    {currentPhase?._id
                    && <div className={styles.option} onClick={() => handleDuplicatePhase()}>
                      Dupliquer cette phase
                    </div>
                    }
                  </div>
                </div>}
                </>
              }
            </div>
            {!generalInfosActive && currentPhase && estimate?.logistics?.operationalBase && (
              <PhaseHeaderRates
                logistics={estimate.logistics}
                transport={{
                  trucks: estimate.transport?.trucks,
                  discount: (estimate.transport as { discount?: number })?.discount,
                }}
                hourlyRate={estimate.hourlyRate}
                agencyId={estimate.agency?._id}
                agencies={list.agencies}
              />
            )}
            <div className={styles.tabs}>
              <Routes>
                <Route path='/' element={<GeneralInfos isPreview={isPreview} />} />
                <Route path='/various/:variousId' element={<Various isPreview={isPreview} />} />
                <Route path='/:phaseId' element={<Phase isPreview={isPreview} />} />
              </Routes>
            </div>
            {!isPreview
                && estimate?.worksite?.name
                && estimate?.worksite?.city
                && estimate?.worksite?.department
                && estimate?.logistics?.operationalBase
                && <>
                  {!addStepDown
                    ? <a className={styles.addDown} onMouseOver={() => setAddStepDown(true)}>
                    {BiPlus({})} Ajouter une phase
                  </a>
                    : <div
                    className={styles['addDown-step']}
                    onMouseLeave={() => setAddStepDown(false)}
                  >
                    <div className={styles.select}>
                      <div className={styles.option} onClick={() => handleCreatePhase()}>
                        Phase N° {estimate.phases.length + 1}
                      </div>
                      <div className={styles.option} onClick={() => handleCreatePhase('elevator')}>
                        Phase ascenseur
                      </div>
                      <div className={styles.option} onClick={() => handleCreatePhase('umbrella')}>
                        Phase parapluie
                      </div>

                      <div className={styles.option} onClick={() => handleCreateVarious()}>
                        Phase divers
                      </div>
                      {currentPhase?._id
                      && <div className={styles.option} onClick={() => handleDuplicatePhase()}>
                        Dupliquer cette phase
                      </div>
                      }
                      <div className={styles.label}>
                        {BiPlus({})} Ajouter une phase
                      </div>
                    </div>
                  </div>
                  }
                </>
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default Estimate;
